import { TFixtureChunkAccumulator, TFixtureImport } from '../types';
import { getLineCount } from './transcript';

export const parseEvents = (value: string): Record<string, unknown>[] => {
  const parsed = JSON.parse(value) as unknown;

  if (Array.isArray(parsed)) {
    return parsed.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
  }

  if (parsed && typeof parsed === 'object') {
    return [parsed as Record<string, unknown>];
  }

  return [];
};

export const serializeEvents = (events: Record<string, unknown>[]): string => JSON.stringify(events, null, 2);

export const getFirstEventTranscriptBefore = (events: Record<string, unknown>[]): string =>
  typeof events[0]?.transcriptBefore === 'string' ? events[0].transcriptBefore : '';

export const getStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === 'string');
};

export const getChangedLineNumbers = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    if (Number.isInteger(item)) {
      return [item as number];
    }

    if (item && typeof item === 'object' && Number.isInteger((item as { line?: unknown }).line)) {
      return [(item as { line: number }).line];
    }

    return [];
  });
};

export const getEventChunkIndex = (event: Record<string, unknown>): number | null =>
  Number.isInteger(event.fixtureChunkIndex) ? (event.fixtureChunkIndex as number) : null;

export const readFixtureFiles = async (files: FileList | null): Promise<TFixtureImport> => {
  if (!files?.length) {
    return { chunks: [], events: [], transcript: '' };
  }

  const sortedFiles = Array.from(files).sort((left, right) => {
    const leftPath = left.webkitRelativePath || left.name;
    const rightPath = right.webkitRelativePath || right.name;
    return leftPath.localeCompare(rightPath, undefined, { numeric: true });
  });
  const chunks = new Map<number, TFixtureChunkAccumulator>();
  const looseEvents: Record<string, unknown>[] = [];

  for (const file of sortedFiles) {
    const path = file.webkitRelativePath || file.name;
    const chunkMatch = path.match(/(?:^|\/)chunk-(\d+)\/(.+)$/);

    if (!chunkMatch) {
      if (file.name.endsWith('.json')) {
        const parsed = JSON.parse(await file.text()) as unknown;
        if (parsed && typeof parsed === 'object') {
          looseEvents.push(parsed as Record<string, unknown>);
        }
      }
      continue;
    }

    const chunkIndex = Number(chunkMatch[1]);
    const chunkFilePath = chunkMatch[2];
    const chunk = chunks.get(chunkIndex) ?? { events: [], index: chunkIndex };
    chunks.set(chunkIndex, chunk);

    if (chunkFilePath === 'base.txt') {
      chunk.baseText = await file.text();
      continue;
    }

    if (chunkFilePath === 'final.txt') {
      chunk.finalText = await file.text();
      continue;
    }

    if (chunkFilePath.startsWith('events/') && file.name.endsWith('.json')) {
      const parsed = JSON.parse(await file.text()) as unknown;
      if (parsed && typeof parsed === 'object') {
        chunk.events.push(parsed as Record<string, unknown>);
      }
    }
  }

  let nextStartLine = 1;
  const chunkRanges = Array.from(chunks.values())
    .filter((chunk) => typeof chunk.baseText === 'string')
    .sort((left, right) => left.index - right.index)
    .map((chunk) => {
      const baseText = chunk.baseText ?? '';
      const lineCount = getLineCount(baseText);
      const range = {
        baseText,
        endLine: nextStartLine + lineCount - 1,
        eventCount: chunk.events.length,
        finalText: chunk.finalText ?? '',
        index: chunk.index,
        startLine: nextStartLine,
      };
      nextStartLine += lineCount;
      return range;
    });

  const events = [
    ...looseEvents,
    ...Array.from(chunks.values())
      .sort((left, right) => left.index - right.index)
      .flatMap((chunk) =>
        chunk.events.map((event) => ({
          ...event,
          fixtureChunkIndex: chunk.index,
        })),
      ),
  ];

  return {
    chunks: chunkRanges,
    events,
    transcript: chunkRanges.map((chunk) => chunk.baseText).join('\n'),
  };
};
